'use client'

import React, { useMemo, memo } from 'react'
import { CompanyInfoProps } from '@/types/company'
import Follow from '@/components/Shared/Follow'
import ActionButtonCompany from '@/components/Shared/ActionButtonCompany'
import SnsIconSet from '@/components/Shared/SnsIconSet'
import CompanyRecruitLinks from '@/components/Shared/Company/CompanyRecruitLinks'
import CategoryLabels from '@/components/Shared/CategoryLabels'
import styles from './styles.module.scss'
import clsx from 'clsx'
import RoleModelFinderBanner from '../../RoleModelFinderBanner'

const CompanyCommonInfo = memo(function CompanyCommonInfo({
  companyData,
  used,
  showRoleModelFinderBanner,
}: CompanyInfoProps) {
  const isHeader = used === 'header'
  const isFooter = used === 'footer'

  const categories = useMemo(() => {
    return companyData.categories?.filter(Boolean) ?? []
  }, [companyData])

  const socialMediaUrls = useMemo(() => {
    const urls = [
      { name: 'Facebook', url: companyData.facebook, icon: 'facebook' },
      { name: 'Twitter', url: companyData.twitter, icon: 'x' },
      { name: 'YouTube', url: companyData.youtube, icon: 'youtube' },
      { name: 'Instagram', url: companyData.instagram, icon: 'instagram' },
      { name: 'Wantedly', url: companyData.wantedly, icon: 'wantedly' },
      { name: 'LinkedIn', url: companyData.linkedin, icon: 'linkedin' },
      { name: 'TikTok', url: companyData.tiktok, icon: 'tiktok' },
      { name: 'LINE', url: companyData.line, icon: 'line' },
    ]
    return urls.filter((sns): sns is { name: string; url: string; icon: string } =>
      Boolean(sns.url),
    )
  }, [companyData])

  return (
    <div className={clsx(styles.companyInfoWrapper, styles[used], used)}>
      <dl className={styles.companyInfo}>
        <dt className={styles.companyInfoLogos}>
          <img
            src={companyData.imageUrl || '/images/svg/dummy_logo.svg'}
            className={styles.logoImage}
            alt={companyData.name}
            loading='lazy'
          />
          {isFooter && (
            <Follow followText='フォロー' type='company' item={companyData} used='company' />
          )}
        </dt>

        <dd className={styles.companyInfoContents}>
          <div className={styles.companyInfoContentsInner}>
            <div className={styles.companyInfoBody}>
              <h2 className={styles.companyName}>{companyData.name}</h2>

              {isFooter && <CategoryLabels categories={categories} className={styles.category} />}

              {companyData.summary && <p className={styles.companyDesc}>{companyData.summary}</p>}

              <CompanyRecruitLinks links={companyData.links} className='spnone' />

              <SnsIconSet socialMediaUrls={socialMediaUrls} className='spnone' />
            </div>

            <div className={clsx(styles.companyInfoButton, 'spnone')}>
              {isHeader && (
                <Follow followText='フォロー' type='company' item={companyData} used='company' />
              )}
              {isFooter && companyData.actionBtns && companyData.actionBtns.length > 0 && (
                <ActionButtonCompany
                  companyId={companyData.id}
                  actionButtons={companyData.actionBtns}
                  companyName={companyData.name}
                />
              )}
            </div>

            {isHeader && showRoleModelFinderBanner && (
              <RoleModelFinderBanner from='company' companyId={companyData.id} className='spnone' />
            )}
          </div>
        </dd>
      </dl>

      {/* Social media icons */}
      <div className={styles.companyInfoSns}>
        <CompanyRecruitLinks links={companyData.links} />
        <SnsIconSet socialMediaUrls={socialMediaUrls} />
      </div>

      {/* Mobile action buttons */}
      <div className={clsx(styles.companyInfoButton, 'pcnone')}>
        {isHeader && (
          <Follow followText='フォロー' type='company' item={companyData} used='company' />
        )}
        {isFooter && companyData.actionBtns && companyData.actionBtns.length > 0 && (
          <ActionButtonCompany
            companyId={companyData.id}
            actionButtons={companyData.actionBtns}
            companyName={companyData.name}
          />
        )}
      </div>
      {isHeader && showRoleModelFinderBanner && (
        <RoleModelFinderBanner from='company' companyId={companyData.id} className='pcnone' />
      )}
    </div>
  )
})

export default CompanyCommonInfo
